import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";
import { Heading2, Subheading } from "@/components/podkit/typography/Headings";
import { USER_SETTINGS_MOVED_DISMISSED_KEY } from "@/components/settings/SettingsMenu";
import { SidebarButton } from "@/components/SidebarButton";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { TrackLocations } from "@/hooks/use-segment";
import { GitAuthenticationsPage } from "@/routes/settings/user/git-authentications/GitAuthenticationsPage";
import { IntegrationsPage as UserIntegrationsPage } from "@/routes/settings/user/integrations/IntegrationsPage";
import { PersonalAccessTokensPage } from "@/routes/settings/user/personal-access-tokens/PersonalAccessTokensPage";
import { UserProfilePageV2 } from "@/routes/settings/user/profile/UserProfilePageV2";
import { UserSecretsPage } from "@/routes/settings/user/secrets/UserSecretsPage";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconArrowCornerUpLeft } from "central-icons/IconArrowCornerUpLeft";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { createParser, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const USER_SETTINGS_MODAL_QUERY_PARAM = "user-settings";

const USER_SETTINGS_TABS = [
    "profile",
    "secrets",
    "git-authentications",
    "personal-access-tokens",
    "integrations",
] as const;
type UserSettingsTab = (typeof USER_SETTINGS_TABS)[number];

const parseAsUserSettingsTab = createParser<UserSettingsTab>({
    parse(queryValue) {
        if (USER_SETTINGS_TABS.includes(queryValue as UserSettingsTab)) {
            return queryValue as UserSettingsTab;
        }
        return null;
    },
    serialize(value) {
        return value;
    },
});

type TabConfig = {
    id: UserSettingsTab;
    label: string;
    contentLabel?: string;
    description?: string;
    component: FC;
};

// Components wrapped to pass hideDescription prop for modal context
const SecretsPageForModal: FC = () => <UserSecretsPage hideDescription />;
const GitAuthenticationsPageForModal: FC = () => <GitAuthenticationsPage hideDescription />;
const PersonalAccessTokensPageForModal: FC = () => <PersonalAccessTokensPage hideDescription />;
const IntegrationsPageForModal: FC = () => <UserIntegrationsPage hideDescription />;

const tabs: TabConfig[] = [
    { id: "profile", label: "Account", contentLabel: "Account details", component: UserProfilePageV2 },
    {
        id: "secrets",
        label: "Secrets",
        description: "These are your personal secrets that are available in all environments you create.",
        component: SecretsPageForModal,
    },
    {
        id: "git-authentications",
        label: "Git authentications",
        description:
            "Git authentication connects Ona to your source control accounts. This page shows your current GitHub, Bitbucket Cloud, or Azure DevOps connections for both runners and Ona Desktop.",
        component: GitAuthenticationsPageForModal,
    },
    {
        id: "personal-access-tokens",
        label: "Personal access tokens",
        description:
            "Personal access tokens (PATs) are secure credentials that allow API access to Ona without manually logging in. They enable integration with external tools and workflow automation, while providing users the ability to revoke access when needed.",
        component: PersonalAccessTokensPageForModal,
    },
    {
        id: "integrations",
        label: "Integrations",
        description: "Connect your personal accounts to integrations enabled by your organization.",
        component: IntegrationsPageForModal,
    },
];

export const UserSettingsModal: FC = () => {
    const [activeTab, setActiveTab] = useQueryState(USER_SETTINGS_MODAL_QUERY_PARAM, parseAsUserSettingsTab);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [breadcrumbAction, setBreadcrumbAction] = useState<ReactNode>(null);
    const location = useLocation();

    const isOpen = activeTab !== null;
    const isOnOrgSettingsPage = location.pathname.startsWith("/settings");

    // If a user visits the new modal but hasn't dismissed the nudge yet, dismiss it for them
    const {
        value: userSettingsMovedPref,
        isLoading: isUserSettingsMovedPrefLoading,
        setValue: setUserSettingsMovedPref,
    } = usePreferenceValue(USER_SETTINGS_MOVED_DISMISSED_KEY);
    useEffect(() => {
        if (isOpen && !userSettingsMovedPref && !isUserSettingsMovedPrefLoading) {
            setUserSettingsMovedPref("true");
        }
    }, [isOpen, userSettingsMovedPref, isUserSettingsMovedPrefLoading, setUserSettingsMovedPref]);

    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!newOpen) {
                void setActiveTab(null);
                setShowMobileSidebar(false);
                setBreadcrumbAction(null);
            }
        },
        [setActiveTab],
    );

    const handleTabChange = useCallback(
        (tab: UserSettingsTab) => {
            void setActiveTab(tab);
            setShowMobileSidebar(false);
            setBreadcrumbAction(null);
        },
        [setActiveTab],
    );

    const settingsPageContextValue = useMemo(
        () => ({
            setBreadCrumbRowAction: setBreadcrumbAction,
            setCustomBreadcrumbs: () => {
                // No-op in modal context - breadcrumbs don't apply
            },
        }),
        [],
    );

    const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
    const ActiveComponent = activeTabConfig?.component ?? UserProfilePageV2;

    return (
        <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className={cn(
                        "fixed inset-0 z-50 bg-surface-primary/50 backdrop-blur-modal backdrop-opacity-modal",
                        "data-[state=open]:animate-in data-[state=closed]:animate-out",
                        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                    )}
                >
                    <DialogPrimitive.Content
                        className={cn(
                            // Mobile: full screen
                            "fixed inset-0 z-50",
                            // Desktop: centered with max width
                            "md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
                            "md:w-[calc(100%-48px)] md:max-w-5xl",
                            "md:h-[calc(100%-48px)] md:max-h-[800px]",
                            // Layout
                            "flex flex-col overflow-hidden",
                            // Appearance
                            "app-background md:rounded-xl md:border md:border-border-base md:shadow-modal",
                            // Animations
                            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
                            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
                            "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
                            "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
                        )}
                        data-track-location={TrackLocations.UserSettingsModal}
                    >
                        {/* Header */}
                        <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border-base bg-surface-primary px-4 md:px-6">
                            <div className="flex items-center gap-3">
                                {/* Mobile menu button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="size-8 p-0 text-content-muted md:hidden"
                                    aria-label={showMobileSidebar ? "Hide menu" : "Show menu"}
                                    onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                                    data-tracking-id="account-settings-mobile-menu-toggle"
                                >
                                    <MenuIcon />
                                </Button>
                                <DialogPrimitive.Title className="text-lg font-semibold text-content-primary">
                                    User settings
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Description className="sr-only">
                                    Manage your personal settings and preferences.
                                </DialogPrimitive.Description>
                            </div>
                            <DialogPrimitive.Close asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="size-8 p-0 text-content-muted"
                                    aria-label="Close"
                                >
                                    <IconCrossSmall />
                                </Button>
                            </DialogPrimitive.Close>
                        </div>

                        {/* Body */}
                        <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
                            {/* Sidebar - hidden on mobile unless toggled */}
                            <UserSettingsSidebar
                                activeTab={activeTab ?? "profile"}
                                onTabChange={handleTabChange}
                                onClose={() => handleOpenChange(false)}
                                className={cn(
                                    "absolute inset-0 top-14 z-10 md:relative md:inset-auto md:top-auto md:z-auto",
                                    showMobileSidebar ? "block" : "hidden md:flex",
                                )}
                                isMobile={showMobileSidebar}
                                hideOrgSettingsLink={isOnOrgSettingsPage}
                            />

                            {/* Main content - hidden on mobile when sidebar is shown */}
                            <ScrollArea
                                orientation="both"
                                className={cn(
                                    "m-[2px] flex-1 bg-surface-primary p-4 md:p-6",
                                    showMobileSidebar && "hidden md:block",
                                )}
                            >
                                <div className="mb-6 flex items-start justify-between gap-4">
                                    <div>
                                        <Heading2>{activeTabConfig?.contentLabel ?? activeTabConfig?.label}</Heading2>
                                        {activeTabConfig?.description && (
                                            <Subheading className="mt-2">{activeTabConfig.description}</Subheading>
                                        )}
                                    </div>
                                    {breadcrumbAction && <div className="flex-shrink-0">{breadcrumbAction}</div>}
                                </div>
                                <SettingsPageContext.Provider value={settingsPageContextValue}>
                                    <ActiveComponent />
                                </SettingsPageContext.Provider>
                            </ScrollArea>
                        </div>
                    </DialogPrimitive.Content>
                </DialogPrimitive.Overlay>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

const MenuIcon: FC = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M3 5H17M3 10H17M3 15H17"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

type UserSettingsSidebarProps = {
    activeTab: UserSettingsTab;
    onTabChange: (tab: UserSettingsTab) => void;
    onClose: () => void;
    className?: string;
    isMobile?: boolean;
    hideOrgSettingsLink?: boolean;
};

const UserSettingsSidebar: FC<UserSettingsSidebarProps> = ({
    activeTab,
    onTabChange,
    onClose,
    className,
    isMobile,
    hideOrgSettingsLink,
}) => {
    const navigate = useNavigate();

    const handleGoToOrgSettings = useCallback(() => {
        onClose();
        navigate("/settings");
    }, [onClose, navigate]);

    return (
        <div
            className={cn(
                "flex h-full flex-col border-r border-border-base py-4",
                isMobile ? "w-full bg-surface-primary" : "w-[260px] flex-shrink-0",
                className,
            )}
        >
            {/* Back button - mobile only */}
            {isMobile && (
                <button
                    onClick={onClose}
                    className="mx-4 mb-4 flex h-8 flex-row items-center gap-1 rounded-lg pl-2 hover:bg-surface-tertiary"
                    data-tracking-id="account-settings-back-to-ona"
                >
                    <div className="w-5">
                        <IconArrowLeft size="base" />
                    </div>
                    <span className="flex-grow truncate text-start text-base font-bold">Back to Ona</span>
                </button>
            )}
            {/* Go to Organization settings button - hidden when already on org settings page */}
            {!hideOrgSettingsLink && (
                <div className="mb-4 px-4">
                    <button
                        onClick={handleGoToOrgSettings}
                        className="flex w-full flex-row items-center rounded-lg text-content-primary hover:bg-surface-hover"
                        data-tracking-id="user-settings-go-to-org-settings"
                    >
                        <div className="flex size-8 shrink-0 items-center justify-center">
                            <IconArrowCornerUpLeft size={20} />
                        </div>
                        <span className="text-start text-base">Go to Organization settings</span>
                    </button>
                </div>
            )}
            <ScrollArea orientation="vertical" className="mx-[2px] my-[2px] flex-grow pl-[14px]">
                <div className="flex flex-col gap-1 py-0.5">
                    {tabs.map((tab) => (
                        <SidebarButton
                            key={tab.id}
                            label={tab.label}
                            active={activeTab === tab.id}
                            onClick={() => onTabChange(tab.id)}
                            hideLabel={false}
                            data-tracking-id={`account-settings-tab-${tab.id}`}
                        />
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
};
